import React, { useEffect, useState, useMemo } from 'react';
import {
  Checkbox,
  Button,
  Input,
  Space,
  Card,
  Badge,
  Spin,
  Alert,
  Row,
  Col,
  Typography,
  Divider
} from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { trpc as api } from '@/lib/api';

const { Text } = Typography;
const { Search } = Input;

interface TemplateEquipment {
  equipmentId: string;
  equipmentName: string;
  isMandatory: boolean;
  specificInstructions?: string | null;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

interface EquipmentSelectorProps {
  templateId: string | null;
  value: string[];
  onChange: (selectedIds: string[]) => void;
  disabled?: boolean;
  showSearch?: boolean;
  showDetails?: boolean;
  showValidation?: boolean;
  displayMode?: 'list' | 'grid';
  onValidate?: (result: ValidationResult) => void;
}

export const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({
  templateId,
  value,
  onChange,
  disabled = false,
  showSearch = false,
  showDetails = false,
  showValidation = false,
  displayMode = 'list',
  onValidate
}) => {
  const [searchText, setSearchText] = useState('');
  const [showMandatoryOnly, setShowMandatoryOnly] = useState(false);
  const selectedCount = value.length;

  // テンプレートに関連する設備一覧の取得
  const { data: equipments, isLoading, error } = api.equipment.listByTemplate.useQuery(
    { templateId }
  );
  const equipmentList = useMemo<TemplateEquipment[]>(() => {
    if (!Array.isArray(equipments)) {
      return [];
    }
    return equipments
      .filter(
        (eq): eq is TemplateEquipment => Boolean(eq?.equipmentId && eq?.equipmentName)
      )
      .map((eq) => ({
        equipmentId: eq.equipmentId,
        equipmentName: eq.equipmentName,
        isMandatory: Boolean(eq.isMandatory),
        specificInstructions: eq.specificInstructions ?? null,
      }));
  }, [equipments]);

  // バリデーションミューテーション
  const { mutateAsync: validateSelection } = api.equipment.validateSelection.useMutation();

  // 必須設備の自動選択
  useEffect(() => {
    if (!equipmentList.length || selectedCount > 0) {
      return;
    }

    const mandatoryIds = equipmentList
      .filter(eq => eq.isMandatory)
      .map(eq => eq.equipmentId);

    if (mandatoryIds.length > 0) {
      onChange(mandatoryIds);
    }
  }, [equipmentList, onChange, selectedCount]);

  // バリデーションの実行
  useEffect(() => {
    if (!templateId || !onValidate || value.length === 0) {
      return;
    }

    let isMounted = true;

    validateSelection({
      templateId,
      selectedIds: value
    })
      .then(result => {
        if (isMounted) {
          onValidate(result);
        }
      })
      .catch(error => {
        if (isMounted) {
          onValidate({
            isValid: false,
            errors: [error instanceof Error ? error.message : 'Validation failed']
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [templateId, value, onValidate, validateSelection]);

  // フィルタリングされた設備リスト
  const filteredEquipments = useMemo(() => {
    if (!equipmentList.length) return [];

    return equipmentList.filter(eq => {
      // 検索フィルター
      if (searchText && !eq.equipmentName.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
      // 必須のみフィルター
      if (showMandatoryOnly && !eq.isMandatory) {
        return false;
      }
      return true;
    });
  }, [equipmentList, searchText, showMandatoryOnly]);

  // 選択状態の変更ハンドラー
  const handleSelectChange = (equipmentId: string, checked: boolean) => {
    const equipment = equipmentList.find(eq => eq.equipmentId === equipmentId);

    // 必須設備は選択解除できない
    if (equipment?.isMandatory && !checked) {
      return;
    }

    const newValue = checked
      ? [...value, equipmentId]
      : value.filter(id => id !== equipmentId);

    onChange(newValue);
  };

  // 全選択
  const handleSelectAll = () => {
    const allIds = filteredEquipments.map(eq => eq.equipmentId);
    const newIds = [...new Set([...value, ...allIds])];
    onChange(newIds);
  };

  // 全解除（必須以外）
  const handleDeselectAll = () => {
    const mandatoryIds = equipmentList
      .filter(eq => eq.isMandatory)
      .map(eq => eq.equipmentId);
    onChange(mandatoryIds);
  };

  // バリデーションエラーの取得
  const validationErrors = useMemo(() => {
    if (!showValidation || !equipmentList.length) return [];

    const errors: string[] = [];
    const mandatoryIds = equipmentList
      .filter(eq => eq.isMandatory)
      .map(eq => eq.equipmentId);

    const missingMandatory = mandatoryIds.filter(id => !value.includes(id));
    if (missingMandatory.length > 0) {
      errors.push('必須設備が選択されていません');
    }

    return errors;
  }, [showValidation, equipmentList, value]);

  // テンプレート未選択時の表示
  // ローディング中
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          data-testid="loading-spinner"
        />
      </div>
    );
  }

  // エラー時
  if (error) {
    return (
      <Alert
        message="設備の取得に失敗しました"
        description={(error as { message?: string } | null)?.message ?? '不明なエラー'}
        type="error"
        showIcon
      />
    );
  }

  // 設備アイテムのレンダリング
  const renderEquipmentItem = (equipment: TemplateEquipment) => {
    const isChecked = value.includes(equipment.equipmentId);
    const isDisabled = disabled || equipment.isMandatory;

    return (
      <Card
        key={equipment.equipmentId}
        size="small"
        style={{ marginBottom: 8 }}
        className={displayMode === 'grid' ? 'equipment-grid-item' : ''}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Checkbox
              checked={isChecked}
              disabled={isDisabled}
              onChange={(e) => handleSelectChange(equipment.equipmentId, e.target.checked)}
              aria-label={equipment.equipmentName}
            >
              <Text strong>{equipment.equipmentName}</Text>
            </Checkbox>
            {equipment.isMandatory && (
              <Badge count="必須" style={{ backgroundColor: '#f5222d' }} />
            )}
          </Space>

          {showDetails && equipment.specificInstructions && (
            <Text type="secondary" style={{ marginLeft: 24 }}>
              {equipment.specificInstructions}
            </Text>
          )}
        </Space>
      </Card>
    );
  };

  return (
    <div>
      {!templateId && (
        <Alert
          message="テンプレートを選択してください"
          description="設備の候補が多い場合はテンプレートを選んで絞り込めます。テンプレート未選択でも全設備から選択できます。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {/* ツールバー */}
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        {showSearch && (
          <Search
            placeholder="設備を検索..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        )}

        <Space wrap>
          <Button onClick={handleSelectAll} disabled={disabled}>
            全選択
          </Button>
          <Button onClick={handleDeselectAll} disabled={disabled}>
            全解除
          </Button>
          <Checkbox
            checked={showMandatoryOnly}
            onChange={(e) => setShowMandatoryOnly(e.target.checked)}
          >
            必須のみ表示
          </Checkbox>
        </Space>
      </Space>

      {/* バリデーションエラー */}
      {validationErrors.length > 0 && (
        <Alert
          message="選択エラー"
          description={validationErrors.join('、')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 選択状態サマリー */}
      <div style={{ marginBottom: 16 }}>
        <Text>
          選択中: {value.length}台 / 全{equipmentList.length}台
        </Text>
      </div>

      <Divider />

      {/* 設備リスト */}
      {displayMode === 'list' ? (
        <div className="equipment-list">
          {filteredEquipments.map(renderEquipmentItem)}
        </div>
      ) : (
        <Row gutter={[16, 16]} className="equipment-grid">
          {filteredEquipments.map(equipment => (
            <Col key={equipment.equipmentId} xs={24} sm={12} md={8} lg={6}>
              {renderEquipmentItem(equipment)}
            </Col>
          ))}
        </Row>
      )}

      {/* 検索結果が空の場合 */}
      {filteredEquipments.length === 0 && (
        <Alert
          message="設備が見つかりません"
          description="検索条件に一致する設備がありません。"
          type="info"
          showIcon
        />
      )}
    </div>
  );
};


